import { type UploadedDocument, useUploadAgentProfilePhoto } from '@urbanx/agx.agencies.client';
import { AgxFileUpload } from '@urbanx/agx-ui-components';
import {
  AgxAvatar,
  AgxButton,
  AgxSpacer,
  readFileAsBase64,
  useToast,
} from '@urbanx/agx.ui-components2';

const EMPTY_FILE_DATA: [] = [];
const PROFILE_PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'svg', 'bmp'];

interface ProfilePhotoUploadProps {
  disabled: boolean;
  firstName: string;
  lastName: string;
  photo?: UploadedDocument;
  onPhotoChanged: (_photo?: UploadedDocument) => void;
}

const ProfilePhotoUpload = ({
  disabled,
  firstName,
  lastName,
  photo,
  onPhotoChanged,
}: ProfilePhotoUploadProps) => {
  const { pushToast } = useToast();

  const { uploadAgentProfilePhoto, uploadAgentProfilePhotoPending } = useUploadAgentProfilePhoto({
    onSuccess: (uploadedPhoto) => {
      if (uploadedPhoto) onPhotoChanged(uploadedPhoto);
    },
    onError: () => {
      pushToast({
        type: 'error',
        message: 'Unable to upload profile photo',
      });
    },
  });

  const uploadPhoto = async (file: File) =>
    uploadAgentProfilePhoto({
      fileName: file.name,
      contentType: file.type,
      content: await readFileAsBase64(file),
    });

  const photoActionPending = disabled || uploadAgentProfilePhotoPending;
  const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();

  return (
    <AgxSpacer direction="horizontal" gap="spacing-2xl" contentAlignment="center" wrap>
      {photo?.publicPath ? (
        <AgxAvatar type="image" size="2xl" imageUri={photo.publicPath} />
      ) : (
        <AgxAvatar type="text-dark" size="2xl" text={initials} />
      )}

      <AgxSpacer direction="horizontal" gap="spacing-lg" contentAlignment="center">
        <AgxFileUpload
          id="profile-photo-upload"
          title=""
          fileData={EMPTY_FILE_DATA}
          buttonText={uploadAgentProfilePhotoPending ? 'Uploading...' : photo ? 'Change' : 'Upload'}
          isImageFileType
          uploadFile={uploadPhoto}
          extensions={PROFILE_PHOTO_EXTENSIONS}
          disabled={photoActionPending}
        />
        {photo && (
          <AgxButton
            id="profile-photo-remove"
            text="Remove"
            variant="naked"
            size="medium"
            onClick={() => onPhotoChanged(undefined)}
            disabled={photoActionPending}
          />
        )}
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default ProfilePhotoUpload;
